import React, { useState, useEffect, useRef } from 'react';
import { Archive, Plus } from 'lucide-react';
import $ from 'jquery';
import axios from 'axios';
import 'datatables.net-dt';

// DataTables 및 반응형 확장에 필요한 스타일시트 임포트
import 'datatables.net-dt/css/dataTables.dataTables.css';
import 'datatables.net-responsive-dt/css/responsive.dataTables.css';

// DataTables 반응형 확장 기능 활성화
import 'datatables.net-responsive';

const InventoryPage = ({ selectedFarm, setDialog, planState }) => {
    const tableRef = useRef(null);
    const dtApi = useRef(null);
    const [inventory, setInventory] = useState([]);
    const [newSemenCode, setNewSemenCode] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchInventory = async () => {
        if (!selectedFarm?.id) {
            setLoading(false);
            setError("농장이 선택되지 않았습니다.");
            setInventory([]);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get('/api/inventory', {
                params: { member_id: selectedFarm.id, nation: planState.filters?.country }
            });
            const sortedList = (response.data?.list || []).sort((a, b) => a.seq - b.seq);
            setInventory(sortedList);
        } catch (err) {
            console.error("보유 재고 조회 실패:", err);
            setError("재고 정보를 불러오는 중 오류가 발생했습니다.");
            setInventory([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchInventory();
    }, [selectedFarm.id]);

    const handleAddItem = async (e) => {
        e.preventDefault();
        const codeToAdd = newSemenCode.trim().toUpperCase();
        if (!codeToAdd) {
            setDialog({ isOpen: true, title: "입력 오류", message: "씨수소 번호를 입력해주세요.", onClose: () => setDialog({ isOpen: false }) });
            return;
        }

        try {
            const formData = new FormData();
            formData.append('action', 'insert');
            formData.append('seq', '1');
            formData.append('member_id', selectedFarm.id);
            formData.append('jm_semencode', codeToAdd);
            var response = await axios.post('/api/inventory', formData);
            if (response.data?.status === 'success') {
                setDialog({ isOpen: true, title: "추가 완료", message: "성공적으로 추가되었습니다.", onClose: () => setDialog({ isOpen: false }) });
            } else {
                setDialog({ isOpen: true, title: "추가 실패", message: response.data?.message, onClose: () => setDialog({ isOpen: false }) });
            }
            fetchInventory(); // 목록 새로고침
            //setNewSemenCode('');
        } catch (err) {
            console.error("재고 추가 실패:", err);
            const errorMessage = err.response?.data?.message || "재고 추가 중 오류가 발생했습니다.";
            setDialog({ isOpen: true, title: "추가 실패", message: errorMessage, onClose: () => setDialog({ isOpen: false }) });
        }
    };

    const handleDeleteItem = (item) => {
        setDialog({
            isOpen: true,
            title: "삭제 확인",
            message: `씨수소 '${item.jm_semencode}'를 재고에서 삭제하시겠습니까?`,
            onConfirm: async () => {
                try {
                    const formData = new FormData();
                    formData.append('action', 'delete');
                    formData.append('member_id', selectedFarm.id);
                    formData.append('seq', item.seq);
                    formData.append('jm_semencode', item.jm_semencode);
                    var response = await axios.post('/api/inventory', formData);
                    if (response.data?.status === 'success') {
                        setDialog({ isOpen: true, title: "삭제 완료", message: "성공적으로 삭제되었습니다.", onClose: () => setDialog({ isOpen: false }) });
                    } else {
                        setDialog({ isOpen: true, title: "삭제 실패", message: response.data?.message, onClose: () => setDialog({ isOpen: false }) });
                    }
                    fetchInventory(); // 목록 새로고침
                } catch (err) {
                    console.error("재고 삭제 실패:", err);
                    setDialog({ isOpen: true, title: "삭제 실패", message: "삭제 중 오류가 발생했습니다.", onClose: () => setDialog({ isOpen: false }) });
                }
            },
            onClose: () => setDialog({ isOpen: false }),
        });
    };

    const columns = [
        { title: '단축명호(한글)', data: 'h_short' },
        { title: '단축명호(영어)', data: 'e_short' },
        { title: '씨수소번호', data: 'jm_semencode' },
        { title: '삭제', data: null }
    ];

    useEffect(() => {
        const options = {
            columns: columns,
            responsive: { details: { type: 'inline' } },
            paging: false,
            lengthChange: false,
            searching: false,
            ordering: true,
            info: false,
            language: {
                emptyTable: "보유 재고가 없습니다.",
            },
            columnDefs: [
                { targets: 2, className: 'dt-body-right' }, // '씨수소번호' 우측 정렬
                {
                    targets: 3, // 삭제 버튼
                    responsivePriority: 2,
                    orderable: false,
                    className: 'dt-body-center',
                    width: '60px',
                    render: () => `<button class="datatable-action-button danger-button"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg></button>`
                }
            ],
            drawCallback: function () {
                this.api().responsive.recalc();
            }
        };

        const tableNode = tableRef.current;
        if (!dtApi.current) {
            dtApi.current = $(tableNode).DataTable(options);
        }

        const clickHandler = function () {
            const row = dtApi.current.row($(this).closest('tr'));
            const itemToDelete = row.data();
            if (itemToDelete) {
                handleDeleteItem(itemToDelete);
            }
        };

        $(tableNode).on('click', '.datatable-action-button', clickHandler);

        return () => {
            $(tableNode).off('click', '.datatable-action-button', clickHandler);
            if (dtApi.current && $.fn.DataTable.isDataTable(tableNode)) {
                dtApi.current.destroy();
                dtApi.current = null;
            }
        };
    }, [selectedFarm.id]);

    useEffect(() => {
        if (dtApi.current) {
            dtApi.current.clear().rows.add(inventory).draw();
        }
    }, [inventory]);

    return (
        <div className="page-content" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <h2 className="page-title">정액 즐겨찾기</h2>

            {/* 입력 폼 카드 */}
            <div className="card">
                <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#374151', marginBottom: '1rem' }}>보유정액 추가</h3>
                <form onSubmit={handleAddItem} style={{ display: 'flex', gap: '0.5rem' }}>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <input type="text" value={newSemenCode} onChange={(e) => setNewSemenCode(e.target.value)} className="form-input" placeholder="씨수소 번호 입력 (예: 551JE0205)" />
                        <button type="submit" className="action-button" style={{ flexShrink: 0 }} disabled={!selectedFarm?.id}>
                            <Plus size={18} style={{ marginRight: '0.5rem' }} />
                            추가
                        </button>
                    </div>
                </form>
            </div>

            {/* 데이터 테이블 카드 */}
            <div className="card">
                <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#374151', marginBottom: '0.75rem', display: 'flex', alignItems: 'center' }}>
                    <Archive size={20} style={{ marginRight: '0.5rem' }} />
                    보유 재고 목록 ({inventory.length}건)
                    {loading && <span style={{ marginLeft: '1rem', fontSize: '0.875rem', color: '#6b7280', fontWeight: 'normal' }}>재고 목록을 불러오는 중...</span>}
                </h3>
                {error && !loading && (
                    <div style={{ textAlign: 'center', padding: '2rem', color: '#b91c1c' }}>
                        {error}
                    </div>
                )}
                <div className="table-container">
                    <table ref={tableRef} className="display nowrap" style={{ width: '100%' }}></table>
                </div>
            </div>
        </div>
    );
};

export default InventoryPage;
